import React from 'react';
import {
  Form,
  Input,
  Button,
  Divider,
  Typography,
  Switch,
} from 'antd';
import {
  LockOutlined,
} from '@ant-design/icons';
import useProfileStore from '../../../stores/useProfileStore';
import CountdownButton from '../countdown';
import format from '../../../utils/format';

const { Text } = Typography;

const SecurityTab = ({ form, messageApi }) => {
  const {
    loading,
    tfaRequestModal, tfaCheckModal, tfaMobile, tfaCode, tfaLoading, resetTfaTimer, refTfaOption,
    saveSection, requestVerification, checkVerification, cancelVerification, resendVerification, disableTwoFactor,
    setTfaMobile, setTfaCode, setResetTfaTimer, setTfaRequestModal
  } = useProfileStore();

  const handleSaveSecurity = () => saveSection(['password', 'verifypassword', 'tfa_option'], 'Security', form, messageApi);

  const handleTfaChange = (checked) => {
    if (checked && !refTfaOption) {
      setTfaRequestModal(true);
      handleVerificationRequest();
    } else if (!checked && refTfaOption) {
      // Disabling 2FA
      disableTwoFactor(messageApi, form);
    }
    form.setFieldsValue({ tfa_option: checked });
  };

  const handleVerificationRequest = () => requestVerification(tfaMobile, messageApi, form);
  const handleCancelVerification = () => cancelVerification(messageApi, form);
  const handleVerificationCheck = () => checkVerification(messageApi, form);
  const handleResendVerification = () => resendVerification(messageApi, form);

  return (
    <div style={{ padding: '0 40px', minHeight: '100%' }}>
      <div style={{ maxWidth: 600, padding: '32px 0' }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 24px 0', color: '#263238' }}>Security Settings</h3>
        <Form form={form} layout="vertical">
            <Form.Item 
              name="password" 
              label="New Password" 
              rules={[
                { min: 8, message: 'Minimum of 8 characters' },
                { pattern: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/, message: 'Must include at least one letter, one number, and one symbol.' }
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="New Password" />
            </Form.Item>
            <Form.Item 
              name="verifypassword" 
              label="Confirm Password" 
              dependencies={['password']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Confirm Password" />
            </Form.Item>
            <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Form.Item shouldUpdate noStyle>
              {() => {
                 const password = form.getFieldValue('password');
                 const verify = form.getFieldValue('verifypassword');
                 const hasErrors = form.getFieldsError(['password', 'verifypassword']).some(({ errors }) => errors.length > 0);
                 const disabled = !password || !verify || hasErrors;
                 
                 return (
                   <Button
                      onClick={handleSaveSecurity}
                      loading={loading}
                      type="primary"
                      disabled={disabled}
                      style={disabled ? {} : { backgroundColor: '#4CAF50', borderColor: '#4CAF50' }}
                   >
                     Update Password
                   </Button>
                 );
              }}
            </Form.Item>
            </div>
            <Divider style={{ margin: '24px 0' }} />
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                   <div>
                      <Text strong style={{ display: 'block', color: '#263238' }}>Two-Factor Authentication</Text>
                      <Form.Item shouldUpdate={(prev, curr) => prev.mobile !== curr.mobile} noStyle>
                         {() => {
                            const mobile = form.getFieldValue('mobile');
                            let hasMobile = false;
                            let mobileStr = '';

                            if (mobile && typeof mobile === 'object') {
                               hasMobile = !!(mobile.countryCode && mobile.areaCode && mobile.phoneNumber);
                               if (hasMobile) {
                                   const rawMobile = `+${mobile.countryCode || 1}${mobile.areaCode || ''}${mobile.phoneNumber || ''}`;
                                   mobileStr = format.formatMobileBeauty(rawMobile);
                               }
                            } else if (mobile && typeof mobile === 'string') {
                               // Basic check for string mobile (e.g. from backend before edit)
                               hasMobile = mobile.length > 5; // Minimal length check
                               if (hasMobile) {
                                   mobileStr = format.formatMobileBeauty(mobile);
                               }
                            }
                            
                            // Message Logic
                            let message = "Set a phone number on your profile to enable 2FA.";
                            if (refTfaOption && hasMobile) {
                               message = `2FA is enabled on your account via SMS to ${mobileStr}.`;
                            } else if (hasMobile) {
                               message = `Enable 2FA via SMS to ${mobileStr} for enhanced security.`;
                            }

                            return (
                               <Text type="secondary" style={{ fontSize: 13 }}>
                                 {message}
                               </Text>
                            );
                         }}
                      </Form.Item>
                   </div>
                   <Form.Item shouldUpdate noStyle>
                     {() => {
                        const mobile = form.getFieldValue('mobile');
                        let hasMobile = false;
                        if (mobile && typeof mobile === 'object') {
                            hasMobile = !!(mobile.countryCode && mobile.areaCode && mobile.phoneNumber);
                        } else if (mobile && typeof mobile === 'string') {
                            hasMobile = mobile.length > 5;
                        }
                        
                        return (
                          <Form.Item name="tfa_option" valuePropName="checked" noStyle>
                            <Switch 
                              disabled={!hasMobile || tfaRequestModal} 
                              loading={tfaRequestModal || tfaLoading} 
                              onChange={handleTfaChange} 
                            />
                          </Form.Item>
                        );
                     }}
                   </Form.Item>
               </div>
               
               {/* Inline Verification UI */}
               {tfaRequestModal && (
                 <div style={{ 
                   backgroundColor: '#F7F7F7', 
                   borderRadius: 8, 
                   padding: 16, 
                   border: '1px solid #E6E6E6',
                   display: 'flex',
                   flexDirection: 'column',
                   alignItems: 'center',
                   textAlign: 'center'
                 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Verify your number</Text>
                    <Text style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
                       Enter the verification code sent to {format.formatMobileBeauty(tfaMobile)}
                    </Text>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                     <Input
                          placeholder="Code"
                          value={tfaCode}
                          onChange={(e) => setTfaCode(e.target.value)}
                          style={{ width: 120, letterSpacing: 2, textAlign: 'center' }}
                       />
                       <Button
                          onClick={handleVerificationCheck}
                          loading={tfaLoading}
                          disabled={!tfaCode}
                          primary
                       >
                          Verify
                       </Button>
                    </div>

                    <div style={{ display: 'flex', marginTop: 16, gap: 8, alignItems: 'center' }}>
                       <CountdownButton
                          key="resend"
                          handleResend={handleResendVerification}
                          resetTfaTimer={resetTfaTimer}
                          handleReset={() => setResetTfaTimer(false)}
                          size="middle"
                       />
                       <Button onClick={handleCancelVerification} secondary>
                          Cancel
                       </Button>
                    </div>
                 </div>
               )}
            </div>
        </Form>
      </div>
    </div>
  );
};

export default SecurityTab;
